import { User } from 'firebase/auth';
import { DocumentReference, Timestamp } from 'firebase/firestore';

/**
 * Enum representing the different types of ATM operations
 */
export enum AtmOperationType {
  WITHDRAWAL = 'withdrawal',
  DEPOSIT = 'deposit',
  BALANCE = 'balance',
  TRANSFER = 'transfer',
  CANCEL = 'cancel',
  CUSTOM = 'custom',
}

/**
 * Enum representing different visual themes for ATMs
 */
export enum AtmTheme {
  DEFAULT = 'default',
  CLASSIC = 'classic',
  MODERN = 'modern',
  RETRO = 'retro',
  DARK = 'dark',
}

/**
 * Enum representing the status of an ATM transaction
 */
export enum AtmTransactionStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

/**
 * Enum representing the status of a shared ATM
 */
export enum ShareStatus {
  ACTIVE = 'active',
  EXPIRED = 'expired',
  INACTIVE = 'inactive',
  MAX_TRANSACTIONS_REACHED = 'maxTransactionsReached',
  INVALID = 'invalid',
}

/**
 * Interface representing a single ATM option/button
 */
export interface AtmOption {
  /** Unique identifier for the option */
  id?: string;
  /** Display text for the option */
  text: string;
  /** Type of operation this option performs */
  type: AtmOperationType;
  /** Monetary amount for withdrawal/deposit operations (in dollars) */
  amount?: number;
  /** Determines if this option is enabled */
  enabled: boolean;
  /** Position/order of this option */
  position?: number;
  /** Custom data for this option */
  customData?: Record<string, any>;
}

/**
 * Settings for the ATM behavior and appearance
 */
export interface AtmSettings {
  /** Whether this ATM requires a PIN */
  requirePin: boolean;
  /** Default PIN for the ATM (if required) */
  defaultPin?: string;
  /** Maximum number of transactions allowed on this ATM */
  maxTransactions?: number;
  /** Custom CSS for styling the ATM */
  customCss?: string;
  /** Animation speed multiplier (1 = normal) */
  animationSpeed?: number;
  /** Whether to show the ATM in the public explore page */
  showInExplore?: boolean;
  /** Custom sounds for ATM operations */
  sounds?: {
    enabled: boolean;
    buttonPress?: string;
    cashDispense?: string;
    error?: string;
    complete?: string;
  };
  /** Custom messages for different states */
  messages?: {
    welcome?: string;
    processing?: string;
    complete?: string;
    error?: string;
  };
}

/**
 * Interface representing an ATM
 */
export interface Atm {
  /** Unique identifier for the ATM */
  id: string;
  /** Name of the ATM */
  name: string;
  /** Optional description */
  description?: string;
  /** User ID of the creator */
  createdBy: string;
  /** Creator's display name */
  createdByName?: string;
  /** Creation timestamp */
  createdAt: Date | Timestamp;
  /** Last update timestamp */
  updatedAt: Date | Timestamp;
  /** List of ATM options/buttons */
  options: AtmOption[];
  /** Visual theme for the ATM */
  theme: AtmTheme;
  /** Whether this ATM is public (visible to others) */
  isPublic: boolean;
  /** Settings for ATM behavior */
  atmSettings: AtmSettings;
  /** Count of all transactions performed on this ATM */
  transactionCount: number;
  /** List of transactions performed on this ATM */
  transactions?: AtmTransaction[];
  /** Reference to the Firestore document */
  ref?: DocumentReference;
  /** Share settings for this ATM */
  shareSettings?: AtmShareSettings[];
}

/**
 * Interface for creating a new ATM (subset of full ATM interface)
 */
export interface CreateAtmDto {
  name: string;
  description?: string;
  options: AtmOption[];
  theme: AtmTheme;
  isPublic: boolean;
  atmSettings: AtmSettings;
}

/**
 * Interface representing a transaction on an ATM
 */
export interface AtmTransaction {
  /** Unique identifier for the transaction */
  id: string;
  /** ID of the ATM */
  atmId: string;
  /** ID of the option selected */
  optionId?: string;
  /** Text description of the option */
  optionText: string;
  /** Type of operation performed */
  type: AtmOperationType;
  /** Amount involved in the transaction (if applicable) */
  amount?: number;
  /** Timestamp of the transaction */
  timestamp: Date | Timestamp;
  /** User ID of the person who performed the transaction (null if anonymous) */
  userId: string | null;
  /** IP address of the client (for anonymous users) */
  ipAddress?: string;
  /** Transaction status */
  status: AtmTransactionStatus;
  /** Error message if the transaction failed */
  error?: string;
  /** Success message */
  message?: string;
  /** Reference to the Firestore document */
  ref?: DocumentReference;
}

/**
 * Interface for settings related to sharing an ATM
 */
export interface AtmShareSettings {
  /** Maximum number of transactions allowed (undefined for unlimited) */
  maxTransactions?: number | null;
  /** Expiration date for this share */
  expiresAt?: Date | Timestamp | null;
  /** Optional access code required to use the shared ATM */
  accessCode?: string | null;
  /** Whether the share owner can view transaction history */
  allowViewHistory?: boolean;
  /** Whether to notify owner on new transactions */
  notifyOnTransaction?: boolean;
}

/**
 * Interface representing a transaction on a shared ATM
 */
export interface SharedAtmTransaction {
  /** Unique identifier for the transaction */
  id: string;
  /** Share ID this transaction belongs to */
  shareId: string;
  /** ID of the option selected */
  optionId?: string;
  /** Text description of the option */
  optionText: string;
  /** Type of operation performed */
  type: AtmOperationType;
  /** Amount involved in the transaction (if applicable) */
  amount?: number;
  /** Timestamp of the transaction */
  timestamp: Date | Timestamp;
  /** User ID of the person who performed the transaction (null if anonymous) */
  userId: string | null;
  /** Transaction status */
  status: AtmTransactionStatus;
  /** Success or error message */
  message?: string;
}

/**
 * Interface representing a shared ATM (enhanced version)
 */
export interface SharedAtm {
  /** Unique share identifier */
  shareId: string;
  /** ID of the parent ATM */
  atmId: string;
  /** Name of the ATM */
  atmName: string;
  /** Description of the ATM */
  description?: string;
  /** ID of the user who owns the ATM */
  ownerId: string;
  /** Name of the user who owns the ATM */
  ownerName: string;
  /** ATM options (full data for the recipient) */
  options: AtmOption[];
  /** ATM theme */
  theme: AtmTheme;
  /** ATM settings */
  atmSettings: AtmSettings;
  /** Share settings */
  shareSettings: AtmShareSettings;
  /** Current transaction count */
  transactionCount: number;
  /** List of transactions on this shared ATM */
  transactions: SharedAtmTransaction[];
  /** Whether this share is currently active */
  isActive: boolean;
  /** Creation timestamp */
  createdAt: Date | Timestamp;
  /** Last update timestamp */
  updatedAt: Date | Timestamp;
}

/**
 * Interface representing ATM metadata used in listings
 */
export interface AtmListItem {
  /** Unique identifier for the ATM */
  id: string;
  /** Name of the ATM */
  name: string;
  /** Optional description */
  description?: string;
  /** User ID of the creator */
  createdBy: string;
  /** Creator's display name */
  createdByName?: string;
  /** Creation timestamp */
  createdAt: Date | Timestamp;
  /** Count of all transactions performed on this ATM */
  transactionCount: number;
  /** Visual theme for the ATM */
  theme: AtmTheme;
  /** Whether this ATM is public (visible to others) */
  isPublic: boolean;
}

/**
 * Interface for ATM filtering and sorting options
 */
export interface AtmFilters {
  /** Search text to filter by name or description */
  searchText?: string;
  /** Filter by theme */
  theme?: AtmTheme;
  /** Sort options */
  sortBy?: 'date' | 'popularity' | 'name';
  /** Sort direction */
  sortDirection?: 'asc' | 'desc';
  /** Only show ATMs created by the user */
  onlyMine?: boolean;
  /** Only show ATMs the user has interacted with */
  onlyInteracted?: boolean;
}
